import cx from 'classnames'
import { Fragment, useEffect, useMemo, useState } from 'react'
import type * as Square from '@square/web-sdk'
import { useFeature } from '@growthbook/growthbook-react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { first } from 'lodash'
import { useDispatch } from 'react-redux'

import { PaymentMethodType } from 'lib/_types'
import {
  useBuyEmplProductMutation,
  useCreateNewCardMutation,
  useGetEmplProductsQuery,
  useGetPaymentMethodsQuery,
  useLazyGetCardsQuery,
  useLazyGetLatestEmplProductPurchaseQuery,
} from 'store/api/yay/payment'
import { addNotification } from 'store/slices/notifications'

import SquarePaymentFormWrapper from 'features/Payment/components/SquarePaymentFormWrapper'
import CreditCardForm from 'features/Payment/components/CreditCardForm'
import TokenDisplay from 'components/Token/TokenDisplay'
import ConfirmBoxBasic from 'components/ConfirmBoxBasic'
import { List, ListItem } from 'components/List'
import Button from 'components/Button'

import './style.scss'

const PaymentWrapper = ({ selectedProduct, onSuccess, paymentMethod }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [completeModal, setCompleteModal] = useState(false)
  const [getLatestPaymentReq] = useLazyGetLatestEmplProductPurchaseQuery()

  const PaymentMethod = useMemo(() => {
    switch (paymentMethod) {
      case 'square':
        return EnterCreditCardForm
      case 'slash':
        return SlashPayment
    }
  }, [paymentMethod])

  const handleError = () => {
    dispatch(
      addNotification({
        message: t('common.error_generic_try_again'),
        type: 'error',
      })
    )
  }

  const pingLatestPayment = (paymentId) => {
    let i = 0

    const interval = setInterval(async () => {
      const res = await getLatestPaymentReq({
        paymentId,
        method: paymentMethod,
      })
      if (res?.data?.status === 'success' || i >= 5) {
        setCompleteModal(true)
        clearInterval(interval)
      }
      i++
    }, 1500)
  }

  return (
    <>
      <PaymentMethod
        selectedProduct={selectedProduct}
        completeModalSetter={setCompleteModal}
        checkPayment={pingLatestPayment}
        handleError={handleError}
      />
      <ConfirmBoxBasic
        title={t('empl_purchase_payment.payment_received')}
        content={t('empl_purchase_payment.payment_received_description')}
        strictClose={true}
        isActive={completeModal}
        confirmButtonText={t('common.ok')}
        onOk={onSuccess}
      />
    </>
  )
}

const EnterCreditCardForm = ({
  selectedProduct,
  completeModalSetter,
  handleError,
  checkPayment,
}) => {
  const { t } = useTranslation()

  const [tokenLoading, setTokenLoading] = useState(false)
  const [addCardReq, addCardRes] = useCreateNewCardMutation()
  const [getCardsReq, getCardsRes] = useLazyGetCardsQuery()
  const [applyPaymentReq, applyPaymentRes] = useBuyEmplProductMutation()
  const [getLatestPaymentReq] = useLazyGetLatestEmplProductPurchaseQuery()

  const [currentToken, setCurrentToken] = useState<Square.TokenResult>()
  const [cardToUse, setCardToUse] = useState(null)

  const handleTokenizeResponse = (
    token: Square.TokenResult,
    verifiedBuyer: Square.VerifyBuyerResponseDetails
  ) => {
    setTokenLoading(true)
    if (token.status === 'OK') {
      setCurrentToken(token)
      getCardsReq()
    }
  }

  const resetFlow = () => {
    setCurrentToken(null)
    setCardToUse(null)
    setTokenLoading(false)
    completeModalSetter(true)
  }

  useEffect(() => {
    if (getCardsRes.isSuccess && currentToken) {
      // check if card already exists in card history (saved cards)
      const tokenCard = currentToken.details?.card
      const foundCard = getCardsRes.data?.cards?.find(
        (existingCard) =>
          existingCard.card_brand === tokenCard.brand &&
          existingCard.last_4 === tokenCard.last4
      )

      setCardToUse(foundCard)

      if (!foundCard && addCardRes.isUninitialized) {
        // if not, add card
        addCardReq({ nonce: currentToken.token })
      }
    } else if (getCardsRes.isError) {
      handleError()
      resetFlow()
    }
  }, [getCardsRes, currentToken])

  useEffect(() => {
    // re-fetch card history (saved cards) when new card added
    if (addCardRes.isSuccess) {
      getCardsReq()
    } else if (addCardRes.isError) {
      handleError()
      resetFlow()
    }
  }, [addCardRes])

  useEffect(() => {
    // apply input card based on card details from saved cards list
    if (cardToUse && currentToken && selectedProduct) {
      console.log(
        `paying with card: ${cardToUse.card_brand} **** **** **** ${cardToUse.last_4}`
      )
      applyPaymentReq({
        cardId: cardToUse.id,
        productId: selectedProduct.id,
        method: 'square',
      })
    }
  }, [cardToUse, currentToken])

  useEffect(() => {
    if (applyPaymentRes.isError) {
      handleError()
      resetFlow()
    } else if (applyPaymentRes.isSuccess) {
      checkPayment(applyPaymentRes.data?.id)
    }
  }, [applyPaymentRes])

  return (
    <div className="CreditCardForm__wrapper">
      <SquarePaymentFormWrapper onTokenReceived={handleTokenizeResponse}>
        <CreditCardForm
          buttonClassName={
            'Button Button--primary Button--fullwidth Button--size-large'
          }
          buttonText={
            tokenLoading || applyPaymentRes.isLoading
              ? t('common.processing')
              : t('empl.buy_empl')
          }
          buttonDisabled={
            tokenLoading || applyPaymentRes.isLoading || !selectedProduct
          }
        />
      </SquarePaymentFormWrapper>
    </div>
  )
}

const SlashPayment = ({
  selectedProduct,
  completeModalSetter,
  handleError,
  checkPayment,
}) => {
  const { t } = useTranslation()

  const [applyPaymentReq, applyPaymentRes] = useBuyEmplProductMutation()
  const [getLatestPaymentReq] = useLazyGetLatestEmplProductPurchaseQuery()
  const [isInProgress, setIsInProgress] = useState(false)

  const handleSlashPayment = (e) => {
    e.preventDefault()

    if (selectedProduct) {
      // POST /v1/web/payments
      applyPaymentReq({ productId: selectedProduct.id, method: 'slash' })
      setIsInProgress(true)
    }
  }

  // https://slash-fi.gitbook.io/docs/integration-guide/integration-guide/window-widget-integration/implement-frontend-and-integrate-apis
  const handlePaymentWindowClosed = async (
    paymentStatus: number,
    callbackParams: any
  ) => {
    if (paymentStatus === 1 && callbackParams?.payment_token) {
      // success
      checkPayment(callbackParams?.payment_token)
    } else if (paymentStatus === 2) {
      // failure
      handleError()
      setIsInProgress(false)
    } else {
      // mysterious third thing
      // see Slash docs above for more details
      // this can occur for various reasons
      // could be unknown error, but could also be user closing window too fast but after payment has completed
      // because we cannot know the payment status in this case (could be successfully completed, could be error) safest is just to reload
      // if success it should show up in transaction history anyway
      window.location.reload()
    }
  }

  useEffect(() => {
    if (applyPaymentRes?.isSuccess) {
      // returns payment URL
      const slashPaymentUrl = applyPaymentRes?.data?.url
      // open payment URL
      window?.SLASH_FIN?.open(slashPaymentUrl, handlePaymentWindowClosed)
    }
  }, [applyPaymentRes])

  return (
    <Button
      onClick={handleSlashPayment}
      size="large"
      disabled={!selectedProduct || isInProgress}
      fluid
    >
      {t('empl.buy_empl')}
    </Button>
  )
}

const PurchaseEMPL = ({ onSuccess }) => {
  const { t } = useTranslation()

  const getEmplProductsRes = useGetEmplProductsQuery()
  const getPaymentMethodsRes = useGetPaymentMethodsQuery()
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [currentPaymentMethod, setCurrentPaymentMethod] =
    useState<PaymentMethodType>()

  const isPaymentMethodsDisplayed = useFeature('yay-18355')?.on

  useEffect(() => {
    if (getPaymentMethodsRes?.data?.length == 1) {
      setCurrentPaymentMethod(first(getPaymentMethodsRes?.data)?.type)
    }
  }, [getPaymentMethodsRes.data])

  return (
    <div className="PurchaseEMPL">
      <div className="PurchaseEMPL__wrapper">
        <div className="PurchaseEMPL__body">
          <div className="PurchaseEMPLProducts__wrapper">
            {getEmplProductsRes.isLoading && <Skeleton height={232} />}
            {getEmplProductsRes.isSuccess && getEmplProductsRes.data && (
              <Fragment>
                <div className="PurchaseEMPLProducts__list">
                  {getEmplProductsRes.data?.products?.map((emplProduct) => {
                    const serializedProduct = {
                      id: emplProduct?.id,
                      name: emplProduct?.name,
                      price: emplProduct?.price,
                      currency: emplProduct?.currency,
                      empl: emplProduct?.amount,
                    }

                    return (
                      <div
                        key={emplProduct.id}
                        className={cx('PurchaseEMPLProducts__item', {
                          'PurchaseEMPLProducts__item--active':
                            selectedProduct &&
                            serializedProduct.id === selectedProduct?.id,
                        })}
                        onClick={() => setSelectedProduct(emplProduct)}
                      >
                        <div className="PurchaseEMPLProducts__item__image">
                          <TokenDisplay
                            type="empl"
                            displaySize="compact"
                            tokenStyle="none"
                          >
                            {serializedProduct?.empl?.toLocaleString()}
                          </TokenDisplay>
                        </div>
                        <div className="PurchaseEMPLProducts__item__price">
                          ¥{serializedProduct?.price?.toLocaleString()}
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="PurchaseEMPLPayment">
                  {isPaymentMethodsDisplayed && (
                    <List
                      className="PurchaseEMPLPayment__row"
                      border={false}
                      selectable
                    >
                      {getPaymentMethodsRes?.isSuccess &&
                        getPaymentMethodsRes?.data?.map((payment) => {
                          const payments = {
                            square: {
                              img: '/images/icon-credit-card.svg',
                              text: t('common.credit_card'),
                            },
                            slash: {
                              img: '/images/icon-slash.png',
                              text: 'Slash',
                            },
                          }

                          return (
                            <ListItem
                              className="PurchaseEMPLPayment__item__wrapper"
                              key={payment?.type}
                              onSelect={() =>
                                setCurrentPaymentMethod(payment?.type)
                              }
                              selected={currentPaymentMethod === payment?.type}
                              leftAlign
                              selectable
                            >
                              <div className="PurchaseEMPLPayment__item__content">
                                <img
                                  className="PurchaseEMPLPayment__item__logo"
                                  alt={payments[payment?.type]?.text}
                                  src={payments[payment?.type]?.img}
                                />
                                <div className="PurchaseEMPLPayment__item__text">
                                  {payments[payment?.type]?.text}
                                </div>
                              </div>
                            </ListItem>
                          )
                        })}
                    </List>
                  )}
                  {currentPaymentMethod && (
                    <PaymentWrapper
                      selectedProduct={selectedProduct}
                      onSuccess={onSuccess}
                      paymentMethod={currentPaymentMethod}
                    />
                  )}
                </div>
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PurchaseEMPL
